import { useEffect, FC } from 'react';
import { gql, useQuery } from '@apollo/client';

import useShopStore from '@app/shop/hooks/useShopStore';
import useAlerts from '@app/shop/hooks/useAlerts';
import { cmsBlockToAlertItem } from '@app/shop/helpers';
import { AlertType } from '@app/shop/components/Alert';
import Alerts from '@app/shop/components/Alerts';

import { ShopStoreNoticesQuery } from './__generated__/ShopStoreNoticesQuery';

const ShopStoreAlerts: FC = () => {
  const { storeCode } = useShopStore();
  const { alerts, addAlerts } = useAlerts([]);

  const storeNoticesResult = useQuery<ShopStoreNoticesQuery>(
    shopStoreNoticesQuery,
    { skip: !storeCode, fetchPolicy: 'cache-first' }
  );

  const storeNotices = storeNoticesResult?.data?.magento2?.cmsBlocks?.items;

  useEffect(() => {
    const notices = storeNotices?.filter(
      /*
      'extends unknown' to work around tsx generics syntax ambiguities:
      https://stackoverflow.com/questions/32696475/typescript-tsx-and-generic-parameters
      */
      <T extends unknown>(notice: T): notice is NonNullable<T> => !!notice
    );

    if (notices?.length) {
      addAlerts(
        notices.map((cmsBlock) =>
          cmsBlockToAlertItem({ type: AlertType.Warning, cmsBlock })
        )
      );
    }
  }, [storeNotices, addAlerts]);

  return <Alerts alerts={alerts} />;
};

export default ShopStoreAlerts;

export const shopStoreNoticesQuery = gql`
  query ShopStoreNoticesQuery {
    magento2 {
      cmsBlocks(identifiers: ["general-notice"]) {
        items {
          title
          identifier
          content
        }
      }
    }
  }
`;
